import React, { useEffect, useRef, useState } from "react";
import { take, takeRight } from "lodash";
import styled from "styled-components";
import { FontWeight, Spacing, background, border } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import { useIsMounted } from "@lkf/hooks";
import Button from "@ui-kit/button";
import Counter from "@ui-kit/format/counter";
import { tr } from "@radarly/i18n";
import { Cluster } from "@radarly/models/insights";
import Keywords from "./keywords";

export interface IProps {
  cluster: Cluster;
  locale: Locale;
  onClickBack?: () => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.Medium};
  padding-bottom: ${Spacing.Large};
  overflow: hidden;
  background: ${background};
  z-index: 299;
  position: sticky;
  > * {
    flex-shrink: 0;
  }
`;

const PostCount = styled.div`
  white-space: nowrap;
  font-weight: ${FontWeight.Bold};
`;

const Separator = styled.div`
  height: 16px;
  width: 1px;
  background-color: ${border};
`;

const ImageClusterDetailHeader = ({ cluster, locale, onClickBack }: IProps) => {
  const ref = useRef(null);
  const { width } = useSize(ref);
  const isMounted = useIsMounted();

  const [visibleKeywords, setVisibleKeywords] = useState<number>();
  const [isVisible, setVisible] = useState(false);

  const hiddenKeywords = cluster.keywords.length - visibleKeywords;

  // Reset visible keywords count on keywords or dimension change
  useEffect(() => {
    setVisibleKeywords(cluster.keywords.length);
  }, [cluster.keywords, width]);

  // Remove a keyword while there are hidden ones
  useEffect(() => {
    if (isMounted()) {
      ref.current.scrollWidth > ref.current.clientWidth + 2
        ? setVisibleKeywords(visibleKeywords - 1)
        : setVisible(true);
    }
  }, [isMounted, width, visibleKeywords]);

  return (
    <Header ref={ref}>
      <Button onClick={onClickBack} icon="arrow-left" variant="tertiary" />
      <PostCount>
        {tr(locale, "IMAGE_CLUSTER::POSTS")}{" "}
        <Counter number={cluster.total} locale={locale} />
      </PostCount>
      <Separator />
      <Keywords
        isVisible={isVisible}
        visibleKeywords={take(cluster.keywords, visibleKeywords)}
        hiddenKeywords={takeRight(cluster.keywords, hiddenKeywords)}
        locale={locale}
      />
    </Header>
  );
};

export default ImageClusterDetailHeader;
